import type React from "react";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Card,
  CardActionArea,
  CardMedia,
  Grid,
  CircularProgress,
  Alert,
} from "@mui/material";
import type { CoverPageTemplate } from "../../types/cover.types";
import apiService from "../../../../service/Api";

interface CoverPageTemplateModalProps {
  open: boolean;
  onClose: () => void;
  onSelectTemplate: (template: CoverPageTemplate) => void;
  bookingType?: "FUNERAL" | "EVENT" | "CONFERENCE";
}

const CoverPageTemplateModal: React.FC<CoverPageTemplateModalProps> = ({
  open,
  onClose,
  onSelectTemplate,
  bookingType,
}) => {
  const [templates, setTemplates] = useState<CoverPageTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const response = await apiService.get<CoverPageTemplate[]>(
          "/api/configurations/proposal-templates/"
        );

        const data = response.data;

        let filteredTemplates = data.filter((template) => template.is_active);

        if (bookingType) {
          const categoryMap: Record<string, string> = {
            FUNERAL: "funeral",
            EVENT: "event",
            CONFERENCE: "conference",
          };

          filteredTemplates = filteredTemplates.filter(
            (template) =>
              template.category.toLowerCase() === categoryMap[bookingType]
          );
        }

        setTemplates(filteredTemplates);
        setError(null);
      } catch (err) {
        console.error("Error fetching templates:", err);
        setError("Failed to load templates. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, [open, bookingType]);

  const handleTemplateClick = (template: CoverPageTemplate) => {
    onSelectTemplate(template);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Typography variant="h5" fontWeight={600}>
          Select Cover Page Template
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Choose a background design and layout for your service proposal cover
          page
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ my: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && templates.length === 0 && (
          <Alert severity="info" sx={{ my: 2 }}>
            No templates available
            {bookingType && ` for ${bookingType.toLowerCase()} proposals`}.
          </Alert>
        )}

        {!loading && !error && templates.length > 0 && (
          <Grid container spacing={3} sx={{ py: 2 }}>
            {templates.map((template) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={template.id}>
                <Card
                  sx={{
                    height: "100%",
                    transition: "transform 0.2s, box-shadow 0.2s",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 6,
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => handleTemplateClick(template)}
                    sx={{ height: "100%" }}
                  >
                    <CardMedia
                      component="img"
                      height="300"
                      image={template.background_image}
                      alt={template.name}
                      sx={{
                        objectFit: "cover",
                        bgcolor: "grey.100",
                      }}
                    />
                    <Box sx={{ p: 2, textAlign: "center" }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {template.name}
                      </Typography>
                      {template.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          {template.description}
                        </Typography>
                      )}
                    </Box>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CoverPageTemplateModal;
